import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Alert,
  Paper,
  Snackbar,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EmployeeInventoryForm = () => {
  const [itemID, setItemID] = useState("");
  const [unassignedItems, setUnassignedItems] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [assignedBy, setAssignedBy] = useState("");
  const [assignmentDate, setAssignmentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [returnDate, setReturnDate] = useState("");
  const [condition, setCondition] = useState("");
  const [employees, setEmployees] = useState([]);
  const [filteredAssignedBy, setFilteredAssignedBy] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUnassignedItems = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${BASE_URL}/api/inventory/unassigned`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setUnassignedItems(data);
          console.log("Unassigned items:", data);
        } else {
          console.error("Failed to fetch unassigned items");
          setError("Failed to fetch unassigned items");
        }
      } catch (err) {
        console.error("Error fetching unassigned items:", err);
        setError("Error fetching unassigned items");
      }
    };

    const fetchEmployees = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${BASE_URL}/api/employees`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setEmployees(data);
          setFilteredAssignedBy(data);
        } else {
          console.error("Failed to fetch employees");
        }
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };

    fetchUnassignedItems();
    fetchEmployees();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess("");

    try {
      const token = sessionStorage.getItem("token");

      const response = await fetch(
        `${BASE_URL}/api/inventory/assign-inventory`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            itemID,
            assignedTo,
            assignedBy,
            assignmentDate,
            returnDate: returnDate || null,
            condition,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess("Inventory item assigned to employee successfully!");
        setItemID("");
        setAssignedTo("");
        setAssignedBy("");
        setAssignmentDate(new Date().toISOString().split("T")[0]);
        setReturnDate("");
        setCondition("");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Error assigning inventory item");
      }
    } catch (err) {
      console.error("Error assigning inventory item:", err);
      setError("Error assigning inventory item");
    }

    setLoading(false);
  };

  const selectSx = {
    borderRadius: "8px",
    fontSize: "0.875rem",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#eef0f6" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#667eea" },
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#CBD5E1" },
      "&.Mui-focused fieldset": { borderColor: "#667eea" },
    },
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Assign Inventory"
        subtitle="Assign inventory items to employees efficiently"
        icon={<AssignmentIcon />}
      />

      {/* Success/Error Snackbar */}
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess("")} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSuccess("")} severity="success" variant="filled" sx={{ borderRadius: "8px" }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ borderRadius: "8px" }}>{error}</Alert>
      </Snackbar>

      <Paper elevation={0} sx={{ border: "1px solid #eef0f6", borderRadius: "16px", p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Item & Employee Selection */}
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e", mb: 2 }}>
            Assignment Details
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Item</InputLabel>
                <Select
                  value={itemID}
                  onChange={(e) => setItemID(e.target.value)}
                  label="Select Item"
                  required
                  sx={selectSx}
                >
                  <MenuItem value=""><em>Select Item</em></MenuItem>
                  {unassignedItems.length > 0 ? (
                    unassignedItems.map((item) => (
                      <MenuItem key={item._id} value={item.itemID}>
                        {item.itemName} ({item.itemID})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>No unassigned items available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Assigned To</InputLabel>
                <Select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  label="Assigned To"
                  required
                  sx={selectSx}
                >
                  <MenuItem value=""><em>Select Employee</em></MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.employeeid} value={emp.employeeid}>
                      {emp.name} ({emp.employeeid})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Assigned By</InputLabel>
                <Select
                  value={assignedBy}
                  onChange={(e) => setAssignedBy(e.target.value)}
                  label="Assigned By"
                  required
                  sx={selectSx}
                >
                  <MenuItem value=""><em>Select Employee</em></MenuItem>
                  {filteredAssignedBy.map((emp) => (
                    <MenuItem key={emp.employeeid} value={emp.employeeid}>
                      {emp.name} ({emp.employeeid})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Condition</InputLabel>
                <Select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  label="Condition"
                  required
                  sx={selectSx}
                >
                  <MenuItem value=""><em>Select Condition</em></MenuItem>
                  <MenuItem value="1">New</MenuItem>
                  <MenuItem value="2">Under maintenance</MenuItem>
                  <MenuItem value="3">Used</MenuItem>
                  <MenuItem value="4">Damaged</MenuItem>
                  <MenuItem value="5">Repaired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Dates */}
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e", mb: 2 }}>
            Dates
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Assignment Date"
                type="date"
                value={assignmentDate}
                onChange={(e) => setAssignmentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split("T")[0] }}
                required
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Return Date"
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: assignmentDate || new Date().toISOString().split("T")[0] }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2, borderTop: "1px solid #f0f2f8" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1,
                boxShadow: "none",
                "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
                "&:disabled": { bgcolor: "#eef0f6", color: "#8b8fa3" },
              }}
            >
              {loading ? "Assigning..." : "Assign Inventory"}
            </Button>
          </Box>
        </form>
      </Paper>
    </PageContainer>
  );
};

export default EmployeeInventoryForm;
